<template>
  <div class="login-index-content">
    <div v-if="modalIntroBrand === 'ELITE'" class="wd-pt-4">
      <v-img class="wd-mx-auto wd-mb-3" max-width="120" cover src="@/assets/images/logo/logo_elite.svg?url" />
      <p class="mb-3" style="text-align: center">
        {{ $t('elite_login_description') }}
      </p>
    </div>
    <div class="wd-pt-2 wd-flex wd-flex-col wd-gap-3 wd-items-center">
      <div>
        <span class="wd-text-sm">{{ loginBlockTitle }}</span>
      </div>

      <div v-if="!isInAppBrowser" class="wd-flex wd-flex-nowrap wd-gap-6 wd-justify-center wd-items-center wd-min-h-10">
        <!-- google gis button -->
        <div ref="google-button" class="wd-w-10 wd-h-10"></div>

        <!-- apple button -->
        <v-btn
          class="login-index__btn login-btn--default"
          :loading="appleLoginLoading"
          color="black50"
          variant="outlined"
          min-width="40"
          width="40"
          :height="40"
          @click="loginWithApple"
        >
          <img src="@/assets/images/icon/apple-brand.svg?url" width="18" height="18" alt="" />
        </v-btn>

        <!-- facebook button -->
        <v-btn
          class="login-index__btn login-btn--default"
          :loading="fbLoginLoading"
          color="black50"
          variant="outlined"
          min-width="40"
          width="40"
          :height="40"
          @click="fbLogin"
        >
          <img src="@/assets/images/icon/facebook-brand.svg?url" width="18" height="18" alt="" />
        </v-btn>
      </div>

      <div class="wd-w-full wd-flex wd-flex-col wd-gap-3 wd-justify-center wd-items-center" :class="{ 'wd-mt-6': !isInAppBrowser }">
        <span v-if="!isInAppBrowser" class="wd-text-sm">{{ $t('login_continue_with_email') }}</span>
        <v-btn
          class="login-index__btn login-btn--default"
          color="black50"
          :loading="loginLoading"
          variant="outlined"
          width="100%"
          :height="40"
          @click="createAccount"
        >
          <span class="wd-text-base">{{ $t('create_account') }}</span>
        </v-btn>
        <v-btn
          class="login-index__btn login-btn--default"
          color="black50"
          :loading="loginLoading"
          variant="outlined"
          width="100%"
          :height="40"
          @click="login"
        >
          <span class="wd-text-base">{{ $t('all_login') }}</span>
        </v-btn>
      </div>
      <div v-if="isInAppBrowser" class="wd-mt-5 wd-p-4 wd-bg-orange10 wd-whitespace-pre-wrap wd-text-base">
        <span>{{ $t('login_in_app_browser_notice') }}</span>
      </div>
    </div>

    <div :class="modalIntroBrand ? 'wd-mt-12' : 'wd-mt-8 sm:wd-mt-10'">
      <v-divider class="wd-mb-4" :thickness="1" color="gray40" opacity="1"></v-divider>
      <v-btn v-if="!isFunBookV2" class="wd-mb-2 wd-text-orange50 wd-text-otempsm" variant="flat" width="100%" :height="24" @click="handleOpen">
        {{ $t('login_page_contact_us') }}
      </v-btn>
      <div class="login-index__footer" v-html="$t('claim_term')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { windowDetector } from '@/utils/utils'
import { useFBLogin } from '@/composition/useLogin'
import { oauthStateSeeds } from '@/view-models/account'
import { usePlatformStore } from '@/store-pinia/platform'

interface Props {
  modalIntroBrand?: string
}

interface Emits {
  changeAuthStep: [step: string]
  success: []
  showConsentStep: [confirm: (userContract: { isTermsAccepted: boolean; isMarketingAllowed: boolean }) => void]
}

const props = withDefaults(defineProps<Props>(), { modalIntroBrand: '' })
const emit = defineEmits<Emits>()

const i18n = useI18n()
const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()
const { isFunBookV2 } = storeToRefs(usePlatformStore())
const { handleOpen } = useCustomerService()

const loginLoading = ref(false)
const fbLoginLoading = ref(false)

const { login: fbDialogLogin, showConsent, handleConsentConfirm: fbHandleConsentConfirm, resetConsent: fbResetConsent } = useFBLogin()

const isInAppBrowser = computed(() => windowDetector.isInAppBrowser)
const loginBlockTitle = computed(() => (isInAppBrowser.value ? i18n.t('login_in_app_browser_email') : i18n.t('login_page_title')))

watch(showConsent, shouldShow => {
  if (shouldShow) {
    emit('showConsentStep', userContract => fbHandleConsentConfirm(userContract))
  }
})

const googleButtonRef = useTemplateRef('google-button')

const {
  showConsent: googleShowConsent,
  handleConsentConfirm: googleHandleConsentConfirm,
  isGoogleReady,
  renderButton: renderGoogleButton,
  resetConsent: googleResetConsent,
} = useGoogleLogin({ onSuccess: () => emit('success') })

watch(googleShowConsent, shouldShow => {
  if (shouldShow) {
    emit('showConsentStep', userContract => googleHandleConsentConfirm(userContract))
  }
})

watch(isGoogleReady, ready => {
  if (!ready || !googleButtonRef.value || isInAppBrowser.value) return

  renderGoogleButton(googleButtonRef.value, {
    text: 'signin_with',
    type: 'icon',
    locale: i18n.locale.value,
  })
})

const {
  loginWithApple,
  showConsent: appleShowConsent,
  handleConsentConfirm: appleHandleConsentConfirm,
  appleLoginLoading,
  resetConsent: appleResetConsent,
} = useAppleLogin({ onSuccess: () => emit('success') })

watch(appleShowConsent, shouldShow => {
  if (shouldShow) {
    emit('showConsentStep', userContract => appleHandleConsentConfirm(userContract))
  }
})

function getRandomString(length: number): string {
  let result = ''
  for (let i = 0; i < length; i++) {
    result += oauthStateSeeds[Math.floor(Math.random() * oauthStateSeeds.length)]
  }
  return result
}

function oauthFacebookLogin() {
  fbLoginLoading.value = true
  const state = getRandomString(10)
  ;(nuxtApp.$cookiz as any).set('fbLoginStateParam', state, { path: '/', maxAge: 300 })
  ;(nuxtApp.$cookiz as any).set('RedirectTo', window.location.href, { path: '/', maxAge: 60 })
  const fbLoginUrl = `https://www.facebook.com/dialog/oauth?client_id=${config.public.FB_APP_ID}&redirect_uri=${window.location.origin}/auth/fb-login&state=${state}&scope=email`
  location.href = fbLoginUrl
}

async function fbLogin() {
  if (windowDetector.isInAppBrowser) {
    oauthFacebookLogin()
    return
  }
  const isLogin = await fbDialogLogin()
  if (!isLogin) return
  emit('success')
}

function createAccount() {
  emit('changeAuthStep', 'sign-up')
}

function login() {
  emit('changeAuthStep', 'login')
}

defineExpose({
  resetConsent: () => {
    fbResetConsent?.()
    googleResetConsent?.()
    appleResetConsent?.()
  },
})
</script>

<style scoped lang="scss">
.login-index-content {
  .login-index {
    &__btn {
      font-size: 17px;
      &.login-btn--default {
        border-color: #dadce0 !important;
        border-radius: 4px !important;
        cursor: pointer;
      }
    }

    &__footer {
      font-size: 13px;
    }
  }
}
</style>
